import { target } from '@github/catalyst';
import type AgGrid from 'ag-grid-enterprise';

declare const AG_GRID_LICENSE_KEY: string;
declare const agGrid: typeof AgGrid;

export type ClientSideGridOptions<Row> = Omit<
  AgGrid.GridOptions<Row>,
  'rowData' | 'rowModelType'
>;

export abstract class ClientSideGridElement<Row> extends HTMLElement {
  @target declare rowDataScript: HTMLScriptElement;
  @target declare gridHost: HTMLElement;

  private grid: AgGrid.GridApi<Row> | undefined;
  private rowData: Row[] | undefined;

  connectedCallback() {
    agGrid.LicenseManager.setLicenseKey(AG_GRID_LICENSE_KEY);
    if (this.grid) {
      return;
    }

    const rowData = (this.rowData ??= this.readRowData());
    this.grid = agGrid.createGrid(
      this.gridHost,
      standardClientSideGridOptions(rowData, this.getGridOptions(rowData))
    );
  }

  disconnectedCallback() {
    this.grid?.destroy();
    this.grid = undefined;
  }

  protected abstract getGridOptions(rowData: Row[]): ClientSideGridOptions<Row>;

  private readRowData() {
    const rowData = JSON.parse(this.rowDataScript.textContent ?? '[]') as Row[];
    this.rowDataScript.remove();
    return rowData;
  }
}

export function standardClientSideGridOptions<Row>(
  rowData: Row[],
  options: ClientSideGridOptions<Row>
): AgGrid.GridOptions<Row> {
  const { defaultColDef, rowClassRules, onFirstDataRendered, ...overrides } =
    options;
  return {
    rowModelType: 'clientSide',
    rowData,
    defaultColDef: {
      sortable: true,
      filter: true,
      resizable: true,
      minWidth: 110,
      suppressHeaderMenuButton: true,
      ...defaultColDef
    },
    rowClassRules: {
      'color-bg-subtle': ({ rowIndex }) => rowIndex % 2 !== 0,
      ...rowClassRules
    },
    cellSelection: true,
    popupParent: document.body,
    getContextMenuItems: () => ['copy', 'copyWithHeaders'],
    onFirstDataRendered:
      onFirstDataRendered ?? (({ api }) => api.sizeColumnsToFit()),
    ...overrides
  };
}
